import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';

import { queryClient } from '@/lib/queryClient';
import { initWebVitals } from '@/lib/webVitals';
import { initNativeApp } from '@/lib/native';
import { deactivateApplicationClient, isMarketingOnlyMode } from '@/lib/marketingOnly';
import { routeTree } from '@/routeTree.gen';

import '@/styles/global.css';

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV ?? 'production',
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
      ? Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE)
      : 0.15,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Privacy: mask ALL text + inputs and block ALL media in error replays.
      // These are Sentry's defaults, but we set them EXPLICITLY because this app
      // renders minors' PII (dancer DOB, allergies) and private message content —
      // an error-triggered replay must never capture that. Stated explicitly so a
      // future SDK default change can't silently start recording it, and so the
      // App Store privacy label can answer Diagnostics "Not linked to identity".
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
  });
}

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: { queryClient },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element #root not found in document');
}

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
);

initWebVitals();
if (isMarketingOnlyMode) {
  void deactivateApplicationClient();
}

// Native (Capacitor) app bootstrap — a no-op in the browser. Runs after the
// app has rendered so OS deep links can drive the live router.
if (!isMarketingOnlyMode) {
  void initNativeApp({
    navigate: (path) => {
      router.history.push(path);
    },
  });
}
